import { faXmark } from "@fortawesome/free-solid-svg-icons";
import classNames from "classnames";
import { Formik, getIn } from "formik";
import { cloneDeep } from "lodash";
import React, { useEffect, useState } from "react";
import { Accordion, Button, Card, Offcanvas } from "react-bootstrap";

import { TCSCreditRelatingToOptions } from "../../../../config/filingConstants";
import { ITaxesCollectedAtSources } from "../../../../config/interfaces";
import {
  TaxesCollectedFormInitValues,
  TaxesCollectedFormSchema
} from "../../../../pages/itr-filing/efiling.config";
import { useAppSelector } from "../../../../redux/Store";
import { ITRFilingSelector } from "../../../../redux/slice/ITRFilingSlice";
import { UtilService } from "../../../../services";
import FASvgIcon from "../../../FASvgIcon";
import ScrollToFirstError from "../../../ScrollToFirstError";
import { SwitchBtnContextAwareToggle } from "../../../accordion";
import { FormField, NumericInputField, SelectInputField, TextInputField } from "../../../fields";

type Props = {
  showCanvas: boolean;
  formValues: ITaxesCollectedAtSources | null;
  handleCloseBtnPress: (salaryData: ITaxesCollectedAtSources | null) => void;
};

const TDSTaxesCollectedAtSourceDetailsForm: React.FC<Props> = (props: Props) => {
  const { showCanvas, handleCloseBtnPress, formValues } = props;

  const { isFilingReadOnly } = useAppSelector(ITRFilingSelector);

  const [initFormValues, setInitFormValues] = useState<ITaxesCollectedAtSources>(
    TaxesCollectedFormInitValues
  );

  useEffect(() => {
    if (showCanvas && formValues) {
      setInitFormValues(() => cloneDeep(formValues));
    } else {
      setInitFormValues(() => cloneDeep(TaxesCollectedFormInitValues));
    }
  }, [showCanvas, formValues]);

  return (
    <>
      <Offcanvas show={showCanvas} placement="end" backdrop={true}>
        <Formik
          enableReinitialize={true}
          initialValues={initFormValues}
          validationSchema={TaxesCollectedFormSchema}
          onSubmit={(values) => handleCloseBtnPress(values)}
        >
          {({
            handleSubmit,
            handleBlur,
            setFieldValue,
            submitCount,
            isValid,
            errors,
            values,
            touched
          }) => (
            <>
              <ScrollToFirstError submitCount={submitCount} isValid={isValid} errors={errors} />

              <Offcanvas.Header>
                <Offcanvas.Title>Tax Collected At Source</Offcanvas.Title>
                <Button
                  variant="primary"
                  className="icon-only hide-offcanvas"
                  onClick={() => handleCloseBtnPress(null)}
                >
                  <FASvgIcon icon={faXmark} />
                </Button>
              </Offcanvas.Header>

              <Offcanvas.Body>
                <section className="mb-5">
                  <div className="section-title-wrap">
                    <h5>
                      Tax Collected At Source
                      <small>
                        Provide this information from TCS certificate provided by deductor
                      </small>
                    </h5>
                  </div>
                  <FormField label="TAN of Collector" isRequired={true} sm={6}>
                    <TextInputField
                      name={`CollectorTAN`}
                      value={values.CollectorTAN}
                      onChange={(value) => setFieldValue(`CollectorTAN`, value.toUpperCase())}
                      onBlur={handleBlur(`CollectorTAN`)}
                      isInvalid={
                        Boolean(getIn(errors, `CollectorTAN`)) && getIn(touched, `CollectorTAN`)
                      }
                      errorMsg={getIn(errors, `CollectorTAN`)}
                      note="Enter 'GGGGG0000G' if you have donated to Government Funds that don't have a PAN"
                    />
                  </FormField>

                  <FormField label="Name of Collector" isRequired={true} sm={6}>
                    <TextInputField
                      name={`CollectorName`}
                      value={values.CollectorName}
                      onChange={(value) => setFieldValue(`CollectorName`, value)}
                      onBlur={handleBlur(`CollectorName`)}
                      isInvalid={
                        Boolean(getIn(errors, `CollectorName`)) && getIn(touched, `CollectorName`)
                      }
                      errorMsg={getIn(errors, `CollectorName`)}
                    />
                  </FormField>

                  <FormField label="Total Tax Collected" isRequired={true} sm={6}>
                    <NumericInputField
                      showLeftIcon={true}
                      name="TotalTaxCollected"
                      value={values.TotalTaxCollected}
                      onChange={(value) => setFieldValue("TotalTaxCollected", Number(value))}
                      onBlur={handleBlur("TotalTaxCollected")}
                      isInvalid={Boolean(errors.TotalTaxCollected) && touched.TotalTaxCollected}
                      errorMsg={errors.TotalTaxCollected}
                      decimalUpto={0}
                      note="Leave it blank if not deducted in the name of assessee."
                    />
                  </FormField>
                </section>

                <section className="mb-5">
                  <Accordion flush activeKey={values.ClaimOrTransferTDSCredits ? "0" : "1"}>
                    <Card className="more-info-card shadow-none overflow-hidden">
                      <Card.Header
                        className={classNames(!values.ClaimOrTransferTDSCredits && "border-0")}
                      >
                        <Card.Title>
                          <span>Claim or Transfer of credits from another assessee</span>
                          <SwitchBtnContextAwareToggle
                            eventKey="0"
                            name="ClaimOrTransferTDSCredits"
                            onChange={(checked: boolean) =>
                              setFieldValue("ClaimOrTransferTDSCredits", checked)
                            }
                          />
                        </Card.Title>
                      </Card.Header>
                      <Accordion.Collapse eventKey="0">
                        <Card.Body className="pb-0">
                          <section>
                            <FormField label="TCS Credit relating to" isRequired={true} sm={6}>
                              <SelectInputField
                                placeholder="Select"
                                name={`TDSCreditRelatingTo`}
                                isOptionArrayOfObject={false}
                                options={TCSCreditRelatingToOptions}
                                value={[values.TDSCreditRelatingTo]}
                                onChange={(selectedOption) => {
                                  if (selectedOption) {
                                    setFieldValue(`TDSCreditRelatingTo`, selectedOption.value);
                                  } else {
                                    setFieldValue(`TDSCreditRelatingTo`, undefined);
                                  }
                                }}
                                onBlur={handleBlur(`TDSCreditRelatingTo`)}
                                isInvalid={
                                  Boolean(getIn(errors, `TDSCreditRelatingTo`)) &&
                                  getIn(touched, `TDSCreditRelatingTo`)
                                }
                                errorMsg={getIn(errors, `TDSCreditRelatingTo`)}
                              />
                            </FormField>

                            <FormField label="TCS Credit" isRequired={true} sm={6}>
                              <NumericInputField
                                showLeftIcon={true}
                                name="TDSCreditAmount"
                                value={values.TDSCreditAmount}
                                onChange={(value) => setFieldValue("TDSCreditAmount", value)}
                                onBlur={handleBlur("TDSCreditAmount")}
                                isInvalid={
                                  Boolean(getIn(errors, `TDSCreditAmount`)) &&
                                  getIn(touched, `TDSCreditAmount`)
                                }
                                errorMsg={getIn(errors, `TDSCreditAmount`)}
                                note="Amount of TDS received or transferred."
                              />
                            </FormField>

                            <FormField label="PAN of Other Person" isRequired={true} sm={6}>
                              <TextInputField
                                name={`PANOfOtherPerson`}
                                value={values.PANOfOtherPerson}
                                onChange={(value) =>
                                  setFieldValue(`PANOfOtherPerson`, value.toUpperCase())
                                }
                                onBlur={(e) => {
                                  handleBlur(e);
                                }}
                                isInvalid={
                                  Boolean(getIn(errors, `PANOfOtherPerson`)) &&
                                  getIn(touched, `PANOfOtherPerson`)
                                }
                                errorMsg={getIn(errors, `PANOfOtherPerson`)}
                              />
                            </FormField>
                          </section>
                        </Card.Body>
                      </Accordion.Collapse>
                    </Card>
                  </Accordion>
                </section>

                <section className="mb-5">
                  <FormField label="Tax Collected claimed for this year" isRequired={true} sm={6}>
                    <NumericInputField
                      showLeftIcon={true}
                      name="TaxCollectedClaimedForThisYear"
                      value={values.TaxCollectedClaimedForThisYear}
                      onChange={(value) =>
                        setFieldValue("TaxCollectedClaimedForThisYear", Number(value))
                      }
                      onBlur={handleBlur("TaxCollectedClaimedForThisYear")}
                      isInvalid={
                        Boolean(errors.TaxCollectedClaimedForThisYear) &&
                        touched.TaxCollectedClaimedForThisYear
                      }
                      errorMsg={errors.TaxCollectedClaimedForThisYear}
                      decimalUpto={0}
                      note="Usually this amount is same as Total Tax Deducted."
                    />
                  </FormField>

                  <FormField
                    label="Expenditure against which TCS was deducted"
                    isRequired={true}
                    sm={6}
                  >
                    <NumericInputField
                      showLeftIcon={true}
                      name="ExpenditureAgainstTDS"
                      value={values.ExpenditureAgainstTDS}
                      onBlur={handleBlur("ExpenditureAgainstTDS")}
                      onChange={(value) => setFieldValue("ExpenditureAgainstTDS", Number(value))}
                      isInvalid={
                        Boolean(errors.ExpenditureAgainstTDS) && touched.ExpenditureAgainstTDS
                      }
                      errorMsg={errors.ExpenditureAgainstTDS}
                      decimalUpto={0}
                    />
                  </FormField>

                  <FormField
                    label="Financial Year in which TCS was deducted"
                    isRequired={true}
                    sm={6}
                  >
                    <SelectInputField
                      placeholder="Select"
                      name={`FinancialYearTDSCollected`}
                      isOptionArrayOfObject={false}
                      options={UtilService.generateYearRanges({ start: 2011 })}
                      value={[values.FinancialYearTDSCollected]}
                      onChange={(selectedOption) => {
                        if (selectedOption) {
                          setFieldValue(`FinancialYearTDSCollected`, selectedOption.value);
                        } else {
                          setFieldValue(`FinancialYearTDSCollected`, undefined);
                        }
                      }}
                      onBlur={handleBlur(`FinancialYearTDSCollected`)}
                      isInvalid={
                        Boolean(getIn(errors, `FinancialYearTDSCollected`)) &&
                        getIn(touched, `FinancialYearTDSCollected`)
                      }
                      errorMsg={getIn(errors, `FinancialYearTDSCollected`)}
                      note="This is the same as financial year of the return (2022-23)"
                    />
                  </FormField>
                </section>
              </Offcanvas.Body>

              <div className="offcanvas-btns">
                {!isFilingReadOnly ? (
                  <Button variant="outline-white" size="sm" onClick={() => handleSubmit()}>
                    Save and Close
                  </Button>
                ) : (
                  <Button
                    variant="outline-white"
                    size="sm"
                    onClick={() => handleCloseBtnPress(null)}
                  >
                    Close
                  </Button>
                )}
              </div>
            </>
          )}
        </Formik>
      </Offcanvas>
    </>
  );
};

export default TDSTaxesCollectedAtSourceDetailsForm;
